import React from 'react';

// Browser snapshots contain responsive/slider DOM that is not safe to hydrate.
// Mount the existing app independently, then switch after its first commit.
export default class PrerenderBoundary extends React.Component {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidMount() {
        this.mounted = true;
        this.frame = requestAnimationFrame(() => {
            this.frame = requestAnimationFrame(() => this.prepareFirstFrame());
        });
    }

    async prepareFirstFrame() {
        const root = document.getElementById('root');
        if (document.getElementById('prerender-shell')) {
            // Only first-screen images and fonts matter here. Never wait for
            // analytics, promotion APIs, offscreen artwork or video downloads.
            const images = [...root.querySelectorAll('img')].filter(image => {
                const box = image.getBoundingClientRect();
                return box.width > 0 && box.height > 0 &&
                    box.bottom > 0 && box.top < window.innerHeight &&
                    box.right > 0 && box.left < window.innerWidth;
            });
            await Promise.race([
                Promise.allSettled([
                    document.fonts?.ready,
                    ...images.map(image => image.decode?.()),
                ]),
                new Promise(resolve => {this.deadline = setTimeout(resolve, 1500);}),
            ]);
            clearTimeout(this.deadline);
        }
        if (!this.mounted || this.state.failed) return;
        this.frame = requestAnimationFrame(() => {
            if (!this.mounted || this.state.failed) return;
            root.removeAttribute('inert');
            root.removeAttribute('aria-hidden');
            document.documentElement.dataset.vieuniteApp = 'ready';
            document.getElementById('prerender-shell')?.remove();
            window.__VIEUNITE_PRERENDER_READY__ = true;
            this.frame = requestAnimationFrame(() => {
                this.frame = requestAnimationFrame(() => {
                    if (!this.mounted || this.state.failed) return;
                    delete document.documentElement.dataset.vieuniteHandoff;
                    window.dispatchEvent(new Event('vieunite:ready'));
                });
            });
        });
    }

    componentDidCatch(error) {
        cancelAnimationFrame(this.frame);
        window.__VIEUNITE_PRERENDER_ERROR__ = error.message;
        console.error('Vieunite could not start:', error);
    }

    componentWillUnmount() {
        this.mounted = false;
        cancelAnimationFrame(this.frame);
        clearTimeout(this.deadline);
    }

    render() {
        if (this.state.failed) {
            // A prerendered page remains readable if startup fails. The ordinary
            // SPA shell still needs a visible error when it has no snapshot.
            return document.getElementById('prerender-shell') ? null :
                <p role="alert">This page could not start. Please reload to try again.</p>;
        }
        return this.props.children;
    }
}
